import { supabase } from './supabase';
import type { EventEntry } from '../types/Entities';
import { createTodo } from './todos';
import { addContactImage } from './images';
import { listEventsCore, createEventCore, updateEventCore, deleteEventCore, getEventOwnerContactIdCore } from '@gv/core';
import { getContactCore, listContactGroupIdsCore } from './contactsCore';
import { getClaimedContactId } from './profiles';

export async function listEvents(contactId: string): Promise<EventEntry[]> {
    // Delegate to core (no fallbacks in dev)
    return (await listEventsCore(supabase as any, contactId)) as any as EventEntry[];
}

export async function createEvent(contactId: string, entry: EventEntry): Promise<EventEntry> {
    // Delegate to core for DB write and normalization
    const savedEvent = (await createEventCore(supabase as any, contactId, entry as any)) as any as EventEntry;

    // If a photo was provided, add it to the contact gallery
    try {
        if (savedEvent.photoUrl) {
            await addContactImage(contactId, savedEvent.photoUrl, { source_type: 'event', source_id: savedEvent.id });
        }
    } catch (e) {
        console.warn('Failed to add event photo to gallery', e);
    }

    // AUTO-CREATE CALENDAR EVENT: Generate calendar entry for the event
    await autoCreateEventCalendarEntry(contactId, savedEvent);

    // NOTE: Replication removed. Caller (e.g., saveAsEvent) is responsible for creating
    // one event per participant contactId so we avoid duplicate rows caused by double logic.
    // This ensures exactly one event per contact appears instead of duplicates.

    return savedEvent;
}

export async function updateEvent(id: string, entry: EventEntry): Promise<EventEntry> {
    // Use core update to enforce consistent data shape and errors
    const updated = (await updateEventCore(supabase as any, id, entry as any)) as any as EventEntry;
    // If a new photo was set, add to gallery
    try {
        if (updated.photoUrl) {
            const ownerId = await getEventOwnerContactIdCore(supabase as any, id);
            if (ownerId) {
                await addContactImage(ownerId, updated.photoUrl, { source_type: 'event', source_id: id });
            }
        }
    } catch (e) {
        console.warn('Failed to sync updated event photo to gallery', e);
    }
    return updated;
}

export async function deleteEvent(id: string): Promise<void> {
    // Delegate to core (no silent fallbacks)
    await deleteEventCore(supabase as any, id);
}

export async function getEventOwnerContactId(eventId: string): Promise<string | null> {
    return await getEventOwnerContactIdCore(supabase as any, eventId);
}

/**
 * Fetch a single contact_event by id and map to EventEntry shape
 */
export async function getEventById(eventId: string): Promise<EventEntry | null> {
    if (!eventId) return null;
    const { data, error } = await (supabase as any)
        .from('contact_events')
        .select('*')
        .eq('id', eventId)
        .maybeSingle();
    if (error) {
        console.error('[events] getEventById failed:', error);
        throw error;
    }
    if (!data) return null;
    const r: any = data;
    const mapped: EventEntry = {
        id: r.id,
        name: r.name,
        whoContactId: r.who_contact_id || undefined,
        whoName: r.who_name || undefined,
        whoAvatar: r.who_avatar || undefined,
        participants: Array.isArray(r.participants) ? r.participants : undefined,
        where: r.where_text || undefined,
        whereMeta: r.where_meta || undefined,
        when: r.when_date || undefined,
        notes: r.notes || undefined,
        entries: Array.isArray(r.entries) ? r.entries : undefined,
        customFields: r.custom_fields || undefined,
        photoUrl: r.photo_url || undefined,
        tasks: Array.isArray(r.tasks) ? r.tasks : [],
    };
    return mapped;
}

/**
 * Auto-create calendar event when a contact event is logged
 * This bridges the gap between "event logging" and "calendar scheduling"
 */
async function autoCreateEventCalendarEntry(contactId: string, event: EventEntry) {
    // Only create calendar entry if event has meaningful scheduling info (when)
    if (!event.when) {
        console.log('🔍 Skipping calendar entry - no date set for event:', event.name);
        return;
    }

    console.log('🔍 Creating follow-up todo for event:', event.name, 'scheduled for:', event.when);

    try {
        // Get contact info for user context and optional group context via membership
        const contact: any = await getContactCore(supabase as any, contactId);
        if (!contact) throw new Error('Contact not found for autoCreateEventCalendarEntry');

        // Build calendar event title
        const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact';
        const title = `${event.name} - ${contactName}`;

        // Parse event date - ensure proper time handling
        let eventDate = new Date(event.when).toISOString();

        // Create description with context
        const description = [
            `Event logged for ${contactName}`,
            event.whoName ? `With: ${event.whoName}` : '',
            event.notes || ''
        ].filter(Boolean).join('\n\n');

        // Resolve My Contact id for assignment
        const myContactId: string | null = await getClaimedContactId(contact.user_id).catch(() => null);

        // Best-effort: find a group context from event participants or owner contact if present
        let groupId: string | null = null;
        try {
            const groupIds = await listContactGroupIdsCore(supabase as any, contactId);
            groupId = (Array.isArray(groupIds) && groupIds.length > 0) ? (groupIds[0] as any) : null;
        } catch { groupId = null; }

        // Create the calendar entry
        const todoData = {
            title,
            description,
            location: event.where || null,
            due_date: eventDate,
            done: false,
            contact_id: event.whoContactId || contactId, // Link to participant or event owner
            event_id: event.id || null, // Link back to the source event
            assigned_contact_id: myContactId || null,
            group_id: groupId
        };

        console.log('🔍 Creating todo with data:', todoData);

        await createTodo(todoData, contact.user_id);

        console.log(`✅ Auto-created calendar entry: "${title}" from contact event`);
    } catch (error) {
        console.error('❌ Failed to auto-create calendar entry from event:', error);
        // Don't throw - event creation should succeed even if calendar entry fails
    }
}
